import React, { useEffect, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useFlowBuilderStore } from '../../../stores/flowBuilderStore';
import { Button, Input } from '../../ui';

interface UpdateCrmConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

type TargetEntity = 'contact' | 'company' | 'deal' | 'lead';

const ENTITY_LABEL: Record<TargetEntity, string> = {
  contact: 'Contato',
  company: 'Empresa',
  deal: 'Negócio',
  lead: 'Lead',
};

type SelectOption = {
  value: string;
  label: string;
};

function normalizeOptions(rawItems: any): SelectOption[] {
  if (!rawItems) {
    return [];
  }

  if (Array.isArray(rawItems)) {
    return rawItems
      .map((item: any) => {
        const value = String(
          item?.ID ?? item?.VALUE ?? item?.value ?? item?.id ?? '',
        ).trim();
        const label = String(
          item?.VALUE ?? item?.NAME ?? item?.label ?? item?.TITLE ?? value,
        ).trim();
        return { value, label };
      })
      .filter((item) => item.value);
  }

  if (typeof rawItems === 'object') {
    return Object.entries(rawItems)
      .map(([key, item]: [string, any]) => {
        if (item && typeof item === 'object') {
          const value = String(
            item?.ID ?? item?.VALUE ?? item?.value ?? item?.id ?? key,
          ).trim();
          const label = String(
            item?.VALUE ?? item?.NAME ?? item?.label ?? item?.TITLE ?? value,
          ).trim();
          return { value, label };
        }
        return {
          value: String(key).trim(),
          label: String(item ?? key).trim(),
        };
      })
      .filter((item) => item.value);
  }

  return [];
}

function parseMultiValue(raw: unknown): string[] {
  const value = String(raw ?? '').trim();
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean);
    }
  } catch {
    // Legacy fallback: comma-separated values.
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function resolveCrmStatusEntityId(
  targetEntity: TargetEntity,
  fieldId: string,
  field: Record<string, any>,
): string | null {
  const explicitCandidates = [
    field?.statusType,
    field?.statusTypeId,
    field?.statusTypeName,
    field?.crmStatusType,
    field?.entityTypeId,
  ];

  for (const candidate of explicitCandidates) {
    const normalized = String(candidate || '').trim().toUpperCase();
    if (normalized) {
      return normalized;
    }
  }

  const normalizedFieldId = String(fieldId || '').trim().toUpperCase();

  if (normalizedFieldId === 'TYPE_ID') {
    return targetEntity === 'deal' ? 'DEAL_TYPE' : 'SOURCE';
  }

  if (normalizedFieldId === 'SOURCE_ID') {
    return 'SOURCE';
  }

  return null;
}

export const UpdateCrmConfig: React.FC<UpdateCrmConfigProps> = ({
  config,
  onChange,
}) => {
  const { bitrixFields } = useFlowBuilderStore();
  const targetEntity: TargetEntity =
    config.targetEntity === 'company' || config.targetEntity === 'deal' || config.targetEntity === 'lead'
      ? config.targetEntity
      : 'contact';
  const mappingMode = config.mappingMode === 'json_auto' ? 'json_auto' : 'manual';

  const fieldMappings = Array.isArray(config.fieldMappings) ? config.fieldMappings : [];

  useEffect(() => {
    if (config.mode !== 'update_crm') {
      onChange('mode', 'update_crm');
    }
  }, [config.mode, onChange]);

  const entityFields = useMemo(() => {
    const fields = (bitrixFields?.[targetEntity] || {}) as Record<string, any>;
    const crmStatusByEntity = (bitrixFields?.crmStatusByEntity || {}) as Record<string, any>;

    return Object.entries(fields)
      .map(([fieldId, field]) => {
        const fieldObject = (field && typeof field === 'object') ? field : {};
        const optionsFromItems = normalizeOptions((fieldObject as any)?.items);

        let options = optionsFromItems;
        if (options.length === 0 && String((fieldObject as any)?.type || '').trim().toLowerCase() === 'crm_status') {
          const statusEntityId = resolveCrmStatusEntityId(
            targetEntity,
            fieldId,
            fieldObject as Record<string, any>,
          );
          options = normalizeOptions(statusEntityId ? crmStatusByEntity[statusEntityId] : undefined);
        }

        return {
          id: fieldId,
          label: (fieldObject as any)?.formLabel || (fieldObject as any)?.title || fieldId,
          options,
          isMultiple:
            (fieldObject as any)?.isMultiple === true ||
            String((fieldObject as any)?.isMultiple || (fieldObject as any)?.multiple || '')
              .trim()
              .toUpperCase() === 'Y',
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [bitrixFields, targetEntity]);

  const fieldMap = useMemo(() => {
    return new Map(entityFields.map((field) => [field.id, field]));
  }, [entityFields]);

  const updateMapping = (index: number, key: 'fieldId' | 'value', value: string) => {
    const next = [...fieldMappings];
    next[index] = { ...next[index], [key]: value };
    onChange('fieldMappings', next);
  };

  const updateFieldId = (index: number, fieldId: string) => {
    const next = [...fieldMappings];
    next[index] = {
      ...next[index],
      fieldId,
      value: '',
    };
    onChange('fieldMappings', next);
  };

  const addMapping = () => {
    onChange('fieldMappings', [
      ...fieldMappings,
      {
        id: crypto.randomUUID(),
        fieldId: '',
        value: '',
      },
    ]);
  };

  const removeMapping = (index: number) => {
    const next = [...fieldMappings];
    next.splice(index, 1);
    onChange('fieldMappings', next);
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-200 leading-relaxed">
        <strong className="text-blue-800 dark:text-blue-100">Editar CRM:</strong> atualiza
        um contato, empresa, negócio ou lead no Bitrix24.
        <br />
        Use o ID retornado pelo nó <strong className="text-blue-800 dark:text-blue-100">Buscar Cliente</strong>,
        por exemplo <strong className="text-blue-800 dark:text-blue-100">{'{{found_entity_id}}'}</strong>.
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
          Entidade para editar
        </label>
        <select
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={targetEntity}
          onChange={(e) => onChange('targetEntity', e.target.value)}
        >
          <option value="contact">Contato</option>
          <option value="company">Empresa</option>
          <option value="deal">Negócio</option>
          <option value="lead">Lead</option>
        </select>
      </div>

      <Input
        label={`ID do ${ENTITY_LABEL[targetEntity]} para editar`}
        placeholder="Ex: {{found_entity_id}}"
        value={config.entityId ?? '{{found_entity_id}}'}
        onChange={(e) => onChange('entityId', e.target.value)}
        helperText="Obrigatório."
      />

      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
          Modo de atualização
        </label>
        <select
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={mappingMode}
          onChange={(e) => {
            const nextMode = e.target.value === 'json_auto' ? 'json_auto' : 'manual';
            onChange('mappingMode', nextMode);
            if (nextMode === 'json_auto' && !String(config.jsonSourceVariable || '').trim()) {
              onChange('jsonSourceVariable', '{{parsed_json}}');
            }
          }}
        >
          <option value="manual">Manual (campo por campo)</option>
          <option value="json_auto">JSON automático (IA)</option>
        </select>
      </div>

      {mappingMode === 'json_auto' ? (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-3 dark:bg-slate-900/20">
          <h4 className="text-xs font-semibold text-slate-800 dark:text-slate-100 uppercase tracking-wide">
            Atualização automática por JSON
          </h4>

          <Input
            label="Variável com JSON de entrada"
            placeholder="Ex: {{parsed_json}}"
            value={config.jsonSourceVariable ?? '{{parsed_json}}'}
            onChange={(e) => onChange('jsonSourceVariable', e.target.value)}
            helperText="Obrigatório no modo JSON automático."
          />

          <label className="inline-flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={config.jsonSkipEmpty !== false}
              onChange={(e) => onChange('jsonSkipEmpty', e.target.checked)}
            />
            Ignorar valores vazios do JSON
          </label>

          <p className="text-[11px] text-slate-500 dark:text-slate-300 leading-relaxed">
            O nó tenta casar cada chave do JSON com o <strong>FIELD_ID</strong> ou com o <strong>nome do campo</strong> do Bitrix24.
            Exemplo: <code>{'{"nome":"Ana","cidade":"Recife"}'}</code>.
          </p>
        </div>
      ) : (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-3 dark:bg-slate-900/20">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold text-slate-800 dark:text-slate-100 uppercase tracking-wide">
              Campos para atualizar ({ENTITY_LABEL[targetEntity]})
            </h4>
            <Button variant="outline" size="sm" onClick={addMapping}>
              <Plus className="w-3 h-3 mr-1" />
              Campo
            </Button>
          </div>

          {fieldMappings.length === 0 ? (
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Adicione ao menos um campo para atualização.
            </p>
          ) : (
            <div className="space-y-2">
              {fieldMappings.map((mapping: any, index: number) => (
                <div key={mapping.id || index} className="grid grid-cols-12 gap-2 items-center">
                  <select
                    className="col-span-5 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
                    value={mapping.fieldId || ''}
                    onChange={(e) => updateFieldId(index, e.target.value)}
                  >
                    <option value="">Selecione um campo</option>
                    {entityFields.map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.label} ({field.id})
                      </option>
                    ))}
                  </select>

                  {(() => {
                    const selectedField = fieldMap.get(String(mapping.fieldId || '').trim());
                    const options = selectedField?.options || [];
                    const isSelectable = options.length > 0;

                    if (isSelectable && selectedField?.isMultiple) {
                      const selectedValues = parseMultiValue(mapping.value);
                      return (
                        <select
                          multiple
                          className="col-span-6 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md min-h-[72px]"
                          value={selectedValues}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions).map((option) => option.value);
                            updateMapping(index, 'value', JSON.stringify(selected));
                          }}
                        >
                          {options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      );
                    }

                    if (isSelectable) {
                      return (
                        <select
                          className="col-span-6 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
                          value={mapping.value || ''}
                          onChange={(e) => updateMapping(index, 'value', e.target.value)}
                        >
                          <option value="">Selecione um valor</option>
                          {options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      );
                    }

                    return (
                      <input
                        type="text"
                        className="col-span-6 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
                        value={mapping.value || ''}
                        onChange={(e) => updateMapping(index, 'value', e.target.value)}
                        placeholder="Valor (aceita variáveis {{...}})"
                      />
                    );
                  })()}

                  <button
                    className="col-span-1 inline-flex justify-center text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    onClick={() => removeMapping(index)}
                    title="Remover campo"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {entityFields.length === 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Não foi possível carregar os campos de {ENTITY_LABEL[targetEntity].toLowerCase()} para esta conexão.
            </p>
          )}
        </div>
      )}

      <p className="text-[11px] text-slate-500 dark:text-slate-300">
        ID editado disponível automaticamente em <code>{'{{updated_entity_id}}'}</code>.
      </p>

      <Input
        label="Variável para o objeto atualizado (opcional)"
        placeholder="Ex: updated_entity_data"
        value={config.responseVariable || ''}
        onChange={(e) => onChange('responseVariable', e.target.value.replace(/\s/g, '_'))}
      />
    </div>
  );
};
